import React, { useState } from "react";
import { Paper, LoadingOverlay, Group, Stack, Alert, Text, Box, Title, ActionIcon, Popover, Button, Modal, Code, ScrollArea } from "@mantine/core";
import { MdInfo, MdFullscreen, MdFullscreenExit, MdFilterListOff, MdDeveloperMode, MdClose } from "react-icons/md";
import { PopoverProvider, usePopoverManager } from "./PopoverManager";
import { DeveloperTools } from "./DeveloperTools";
import { useUserRoles } from "../../../hooks";

interface InfoPopoverProps {
    title?: string;
    content: string;
    popoverId: string;
}

const InfoPopover: React.FC<InfoPopoverProps> = ({ title, content, popoverId }) => {
    const { isPopoverOpen, togglePopover, setOpenPopover } = usePopoverManager();
    const opened = isPopoverOpen(popoverId);

    const handleClose = () => {
        // Only close if this popover is actually the one that's open
        if (opened) {
            setOpenPopover(null);
        }
    };

    return (
        <Popover opened={opened} position="bottom-start" withArrow onClose={handleClose} closeOnClickOutside={true} closeOnEscape={true}>
            <Popover.Target>
                <ActionIcon variant="subtle" color="gray" size="sm" onClick={() => togglePopover(popoverId)}>
                    <MdInfo size={16} />
                </ActionIcon>
            </Popover.Target>
            <Popover.Dropdown>
                <Box p="sm" style={{ maxWidth: 300 }}>
                    <Text size="sm" fw={500} mb="xs">
                        {title}
                    </Text>
                    <Text size="xs">{content}</Text>
                </Box>
            </Popover.Dropdown>
        </Popover>
    );
};

interface ChartScaffoldProps {
    title?: string;
    showTitle?: boolean;
    loading: boolean;
    error?: string | null;
    empty?: boolean;
    emptyMessage?: string;
    stats?: React.ReactNode;
    controls?: React.ReactNode;
    info?: React.ReactNode;
    height?: number;
    children: React.ReactNode;
    onRefresh?: () => void;
    infoContent?: string;
    expandable?: boolean;
    expandedHeight?: number | string;
    onResetFilters?: () => void;
    showResetFilters?: boolean;
    sqlQuery?: string;
    chartData?: any;
    showDeveloperTools?: boolean;
    defaultDaysBack?: number;
}

interface ChartScaffoldContentProps extends ChartScaffoldProps {
    isExpanded: boolean;
    setIsExpanded: (expanded: boolean) => void;
}

// Inner component that has access to popover manager
const ChartScaffoldInner: React.FC<ChartScaffoldContentProps> = ({
    title,
    showTitle = true,
    loading,
    error,
    empty,
    emptyMessage = "No data available for the selected range.",
    stats,
    controls,
    info,
    height = 400,
    children,
    onRefresh,
    infoContent,
    expandable = true,
    expandedHeight = "calc(100vh - 120px)",
    isExpanded,
    setIsExpanded,
    onResetFilters,
    showResetFilters = true,
    sqlQuery,
    chartData,
    showDeveloperTools = false
}) => {
    const { closeAllPopovers } = usePopoverManager();
    const [showDeveloper, setShowDeveloper] = useState(false);
    const { isAdmin } = useUserRoles();

    const handleExpand = () => {
        closeAllPopovers();
        setIsExpanded(true);
    };

    const handleClose = () => {
        setIsExpanded(false);
    };

    const handleResetFilters = () => {
        if (onResetFilters) {
            onResetFilters();
        }
    };

    const handleToggleDeveloper = () => {
        setShowDeveloper(!showDeveloper);
    };

    const copyToClipboard = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text);
        } catch (err) {
            console.error("Failed to copy to clipboard:", err);
        }
    };

    const renderContent = (isExpandedView = false) => {
        if (error) {
            return (
                <Alert icon={<MdInfo size="1rem" />} title="Error Loading Data" color="red">
                    {error}
                    {onRefresh && (
                        <Group mt="xs">
                            <Button size="xs" variant="light" onClick={onRefresh}>
                                Try Again
                            </Button>
                        </Group>
                    )}
                </Alert>
            );
        }

        if (!loading && empty) {
            return (
                <Alert icon={<MdInfo size="1rem" />} title="No Data" color="blue">
                    {emptyMessage}
                </Alert>
            );
        }

        const chartHeight = isExpandedView ? expandedHeight : height;
        return (
            <Stack gap="md" style={{ height: "100%", width: "100%" }}>
                {showDeveloper && (sqlQuery || chartData) && <DeveloperTools sqlQuery={sqlQuery} chartData={chartData} onClose={handleToggleDeveloper} />}
                <Box style={{ height: chartHeight, width: "100%" }}>{children}</Box>
            </Stack>
        );
    };

    const renderHeader = (isExpandedView = false) => (
        <Group justify="space-between" align="flex-start" gap="md">
            <Stack gap={4} style={{ flex: 1 }}>
                {showTitle && title && (
                    <Group gap="xs" align="center">
                        <Title order={isExpandedView ? 3 : 4}>{title}</Title>
                        {infoContent && <InfoPopover title={title} content={infoContent} popoverId={`info-${isExpandedView ? "expanded" : "normal"}`} />}
                    </Group>
                )}
                {!isExpandedView && stats}
            </Stack>
            <Group gap="xs">
                {showDeveloperTools && isAdmin && (sqlQuery || chartData) && (
                    <ActionIcon
                        variant="subtle"
                        color="gray"
                        size="sm"
                        onClick={handleToggleDeveloper}
                        title={showDeveloper ? "Hide developer tools" : "Show developer tools"}
                    >
                        <MdDeveloperMode size={16} />
                    </ActionIcon>
                )}
                {showResetFilters && onResetFilters && (
                    <ActionIcon variant="subtle" color="gray" size="sm" onClick={handleResetFilters} title="Reset filters to default">
                        <MdFilterListOff size={16} />
                    </ActionIcon>
                )}
                {!isExpandedView && controls}
                {!isExpandedView && expandable && (
                    <ActionIcon variant="subtle" color="gray" size="sm" onClick={handleExpand} title="Expand chart">
                        <MdFullscreen size={16} />
                    </ActionIcon>
                )}
            </Group>
        </Group>
    );

    return (
        <>
            <Paper p="md" withBorder radius="lg" shadow="sm" style={{ position: "relative" }}>
                <LoadingOverlay visible={loading} />
                <Stack gap="md">
                    {(showTitle || stats || controls || expandable || (showResetFilters && onResetFilters)) && renderHeader()}
                    {info}
                    {renderContent()}
                </Stack>
            </Paper>

            {/* Expanded Modal */}
            <Modal
                opened={isExpanded}
                onClose={handleClose}
                size="100%"
                withCloseButton={false}
                centered
                fullScreen
                styles={{
                    content: {
                        height: "100vh",
                        width: "100vw",
                        maxHeight: "100vh",
                        maxWidth: "100vw"
                    },
                    body: {
                        padding: 0,
                        height: "100vh",
                        overflow: "hidden"
                    },
                    header: {
                        display: "none"
                    }
                }}
            >
                <PopoverProvider>
                    <Box p="md" style={{ height: "100%", overflow: "auto" }}>
                        <LoadingOverlay visible={loading} />
                        <Stack gap="md" style={{ height: "100%" }}>
                            {/* Custom header with all functionality */}
                            <Group justify="space-between" align="flex-start" gap="md">
                                <Stack gap={4} style={{ flex: 1 }}>
                                    {showTitle && title && (
                                        <Group gap="xs" align="center">
                                            <Title order={3}>{title}</Title>
                                            {infoContent && <InfoPopover title={title} content={infoContent} popoverId="info-expanded-modal" />}
                                        </Group>
                                    )}
                                    {stats}
                                </Stack>
                                <Group gap="xs">
                                    {showDeveloperTools && isAdmin && (sqlQuery || chartData) && (
                                        <ActionIcon
                                            variant="subtle"
                                            color="gray"
                                            size="sm"
                                            onClick={handleToggleDeveloper}
                                            title={showDeveloper ? "Hide developer tools" : "Show developer tools"}
                                        >
                                            <MdDeveloperMode size={16} />
                                        </ActionIcon>
                                    )}
                                    {showResetFilters && onResetFilters && (
                                        <ActionIcon variant="subtle" color="gray" size="sm" onClick={handleResetFilters} title="Reset filters to default">
                                            <MdFilterListOff size={16} />
                                        </ActionIcon>
                                    )}
                                    {controls}
                                    <ActionIcon variant="subtle" color="gray" size="sm" onClick={handleClose} title="Exit fullscreen">
                                        <MdFullscreenExit size={16} />
                                    </ActionIcon>
                                </Group>
                            </Group>
                            {info}
                            {renderContent(true)}
                        </Stack>
                    </Box>
                </PopoverProvider>
            </Modal>
        </>
    );
};

// Content component that wraps the inner component with PopoverProvider
const ChartScaffoldContent: React.FC<ChartScaffoldContentProps> = props => {
    return (
        <PopoverProvider>
            <ChartScaffoldInner {...props} />
        </PopoverProvider>
    );
};

export const ChartScaffold: React.FC<ChartScaffoldProps> = props => {
    const [isExpanded, setIsExpanded] = useState(false);

    return <ChartScaffoldContent {...props} isExpanded={isExpanded} setIsExpanded={setIsExpanded} />;
};
